import { Navigate, Route, Routes } from 'react-router';
import { useAuth } from '@/auth/auth-context';
import { AppShell } from '@/layouts/app-shell';
import { LoginPage } from '@/pages/login-page';
import { RegisterPage } from '@/pages/register/register-page';
import { MfaChallengePage } from '@/pages/mfa-challenge-page';
import { ForgotPasswordPage } from '@/pages/forgot-password-page';
import { ResetPasswordPage } from '@/pages/reset-password-page';
import { VerifyEmailPage } from '@/pages/verify-email-page';
import { UnsubscribePage } from '@/pages/unsubscribe-page';
import { OAuthCallbackPage } from '@/pages/oauth-callback-page';
import { SelectChurchPage } from '@/pages/select-church-page';
import { DashboardPage } from '@/modules/dashboard/dashboard-page';
import { ShortcutsPage } from '@/modules/shortcuts/shortcuts-page';
import { PeopleListPage } from '@/modules/people/people-list-page';
import { PersonDetailPage } from '@/modules/people/person-detail-page';
import { PendingTransfersPage } from '@/modules/people/pending-transfers-page';
import { FollowupPipelinePage } from '@/modules/people/followup-pipeline-page';
import { GrowthStepsPage } from '@/modules/people/growth-steps-page';
import { GrowthReportPage } from '@/modules/people/growth-report-page';
import { PrintDirectoryPage } from '@/modules/people/print-directory-page';
import { PrintLabelsPage } from '@/modules/people/print-labels-page';
import { PrintCredentialPage } from '@/modules/people/print-credential-page';
import { QrCheckInPage } from '@/modules/attendance/qr-check-in-page';
import { GroupsListPage } from '@/modules/groups/groups-list-page';
import { GroupsMapPage } from '@/modules/groups/groups-map-page';
import { GroupsHierarchyPage } from '@/modules/groups/groups-hierarchy-page';
import { GroupDetailPage } from '@/modules/groups/group-detail-page';
import { ItemsListPage } from '@/modules/inventory/items-list-page';
import { ItemDetailPage } from '@/modules/inventory/item-detail-page';
import { CategoriesListPage } from '@/modules/inventory/categories-list-page';
import { LocationsListPage } from '@/modules/inventory/locations-list-page';
import { SuppliersListPage } from '@/modules/inventory/suppliers-list-page';
import { CheckoutsListPage } from '@/modules/inventory/checkouts-list-page';
import { EventsListPage } from '@/modules/events/events-list-page';
import { EventDetailPage } from '@/modules/events/event-detail-page';
import { FormsListPage } from '@/modules/forms/forms-list-page';
import { FormDetailPage } from '@/modules/forms/form-detail-page';
import { AttendanceListPage } from '@/modules/attendance/attendance-list-page';
import { CalendarPage } from '@/modules/calendar/calendar-page';
import { PrintCalendarPage } from '@/modules/calendar/print-calendar-page';
import { KidsListPage } from '@/modules/kids/kids-list-page';
import { SchoolsListPage } from '@/modules/academic/schools-list-page';
import { SchoolDetailPage } from '@/modules/academic/school-detail-page';
import { CourseSectionDetailPage } from '@/modules/academic/course-section-detail-page';
import { MyAcademicPage } from '@/modules/academic/my-academic-page';
import { HomologationsListPage } from '@/modules/academic/homologations-list-page';
import { GivingListPage } from '@/modules/giving/giving-list-page';
import { ChartOfAccountsPage } from '@/modules/finance/chart-of-accounts-page';
import { JournalEntriesPage } from '@/modules/finance/journal-entries-page';
import { AccountLedgerPage } from '@/modules/finance/account-ledger-page';
import { FinancialReportsPage } from '@/modules/finance/financial-reports-page';
import { NotificationsListPage } from '@/modules/notifications/notifications-list-page';
import { CampaignsListPage } from '@/modules/communications/campaigns-list-page';
import { CampaignFormPage } from '@/modules/communications/campaign-form-page';
import { SearchResultsPage } from '@/modules/search/search-results-page';
import { BillingPage } from '@/modules/billing/billing-page';
import { CloudPage } from '@/modules/cloud/cloud-page';
import { AdministrationPage } from '@/modules/administration/administration-page';
import { CustomFieldsPage } from '@/modules/administration/custom-fields-page';
import { RolesPage } from '@/modules/administration/roles-page';
import { RoleFormPage } from '@/modules/administration/role-form-page';
import { AppearancePage } from '@/modules/administration/appearance-page';
import { AppearanceFormPage } from '@/modules/administration/appearance-form-page';
import { SmtpSettingsPage } from '@/modules/platform/smtp-settings-page';
import { StorageSettingsPage } from '@/modules/platform/storage-settings-page';
import { OAuthSettingsPage } from '@/modules/platform/oauth-settings-page';
import { ModuleCatalogPage } from '@/modules/platform/module-catalog-page';
import { SubscriptionsPage } from '@/modules/platform/subscriptions-page';
import { DenominationsPage } from '@/modules/platform/denominations-page';
import { CouncilsPage } from '@/modules/platform/councils-page';
import { MfaSettingsPage } from '@/pages/mfa-settings-page';
import { MySettingsPage } from '@/pages/my-settings-page';
import { GivePage } from '@/pages/give/give-page';
import { GiveSuccessPage } from '@/pages/give/give-success-page';
import { GiveCancelledPage } from '@/pages/give/give-cancelled-page';
import { ManageRecurringPage } from '@/pages/give/manage-recurring-page';
import { PublicSharePage } from '@/pages/public-share-page';
import { NotFoundPage } from '@/pages/not-found-page';

/**
 * Routing.
 *
 * The guards here decide what to *render*. They are not access control: a user
 * who navigates directly to a protected URL is redirected, but a user who calls
 * the API without permission is rejected by the server regardless.
 */
export function AppRouter() {
  const { isAuthenticated, needsTenantSelection } = useAuth();

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/mfa-challenge" element={<MfaChallengePage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/unsubscribe" element={<UnsubscribePage />} />
        <Route path="/oauth-callback" element={<OAuthCallbackPage />} />
        {/* A giving link is meant for a stranger with no account at all —
            it has to work before the catch-all sends every other unknown
            path to `/login`. */}
        <Route path="/give/:tenantSlug" element={<GivePage />} />
        <Route path="/giving/success" element={<GiveSuccessPage />} />
        <Route path="/giving/cancelled" element={<GiveCancelledPage />} />
        <Route path="/give/manage/:token" element={<ManageRecurringPage />} />
        {/* A Mi Nube share link is meant to work for whoever holds it,
            signed in or not — same reasoning as the giving link above. */}
        <Route path="/s/:token" element={<PublicSharePage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Authenticated but no tenant chosen: the user belongs to several churches
  // and every other route would have no scope to run in. A giving link is
  // unrelated to that choice — someone mid-selection for their own church
  // membership can still be opening a donation link for a different one.
  if (needsTenantSelection) {
    return (
      <Routes>
        <Route path="/select-church" element={<SelectChurchPage />} />
        <Route path="/give/:tenantSlug" element={<GivePage />} />
        <Route path="/giving/success" element={<GiveSuccessPage />} />
        <Route path="/giving/cancelled" element={<GiveCancelledPage />} />
        <Route path="/give/manage/:token" element={<ManageRecurringPage />} />
        <Route path="/s/:token" element={<PublicSharePage />} />
        <Route path="*" element={<Navigate to="/select-church" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/shortcuts" element={<ShortcutsPage />} />
        <Route path="/people" element={<PeopleListPage />} />
        <Route path="/people/transfers/pending" element={<PendingTransfersPage />} />
        <Route path="/people/followups/pipeline" element={<FollowupPipelinePage />} />
        <Route path="/people/growth/steps" element={<GrowthStepsPage />} />
        <Route path="/people/growth/report" element={<GrowthReportPage />} />
        <Route path="/people/:id" element={<PersonDetailPage />} />
        <Route path="/groups" element={<GroupsListPage />} />
        <Route path="/groups/map" element={<GroupsMapPage />} />
        <Route path="/groups/hierarchy" element={<GroupsHierarchyPage />} />
        <Route path="/groups/:id" element={<GroupDetailPage />} />
        <Route path="/inventory" element={<ItemsListPage />} />
        <Route path="/inventory/categories" element={<CategoriesListPage />} />
        <Route path="/inventory/locations" element={<LocationsListPage />} />
        <Route path="/inventory/suppliers" element={<SuppliersListPage />} />
        <Route path="/inventory/checkouts" element={<CheckoutsListPage />} />
        <Route path="/inventory/:id" element={<ItemDetailPage />} />
        <Route path="/events" element={<EventsListPage />} />
        <Route path="/events/:id" element={<EventDetailPage />} />
        <Route path="/forms" element={<FormsListPage />} />
        <Route path="/forms/:id" element={<FormDetailPage />} />
        <Route path="/attendance" element={<AttendanceListPage />} />
        <Route path="/attendance/scan" element={<QrCheckInPage />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/kids" element={<KidsListPage />} />
        <Route path="/academic/schools" element={<SchoolsListPage />} />
        <Route path="/academic/schools/:id" element={<SchoolDetailPage />} />
        <Route path="/academic/course-sections/:id" element={<CourseSectionDetailPage />} />
        <Route path="/academic/my" element={<MyAcademicPage />} />
        <Route path="/academic/homologations" element={<HomologationsListPage />} />
        <Route path="/giving" element={<GivingListPage />} />
        <Route path="/finance" element={<ChartOfAccountsPage />} />
        <Route path="/finance/journal-entries" element={<JournalEntriesPage />} />
        <Route path="/finance/accounts/:id/ledger" element={<AccountLedgerPage />} />
        <Route path="/finance/reports" element={<FinancialReportsPage />} />
        <Route path="/notifications" element={<NotificationsListPage />} />
        <Route path="/communications" element={<CampaignsListPage />} />
        <Route path="/communications/new" element={<CampaignFormPage />} />
        <Route path="/communications/:id" element={<CampaignFormPage />} />
        <Route path="/search" element={<SearchResultsPage />} />
        <Route path="/billing" element={<BillingPage />} />
        <Route path="/cloud" element={<CloudPage />} />
        <Route path="/administration" element={<AdministrationPage />} />
        <Route path="/administration/custom-fields" element={<CustomFieldsPage />} />
        <Route path="/administration/roles" element={<RolesPage />} />
        <Route path="/administration/roles/new" element={<RoleFormPage />} />
        <Route path="/administration/roles/:id" element={<RoleFormPage />} />
        <Route path="/administration/appearance" element={<AppearancePage />} />
        <Route path="/administration/appearance/new" element={<AppearanceFormPage />} />
        <Route path="/administration/appearance/:id" element={<AppearanceFormPage />} />
        <Route path="/platform/modules" element={<ModuleCatalogPage />} />
        <Route path="/platform/subscriptions" element={<SubscriptionsPage />} />
        <Route path="/platform/denominations" element={<DenominationsPage />} />
        <Route path="/platform/councils" element={<CouncilsPage />} />
        <Route path="/platform/smtp" element={<SmtpSettingsPage />} />
        <Route path="/platform/storage" element={<StorageSettingsPage />} />
        <Route path="/platform/oauth-providers" element={<OAuthSettingsPage />} />
        <Route path="/security" element={<MfaSettingsPage />} />
        <Route path="/settings" element={<MySettingsPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
      {/* Signing in again while already signed in is not an error. */}
      <Route path="/login" element={<Navigate to="/" replace />} />
      {/* A reset link can arrive while its owner is already signed in
          elsewhere — the page itself needs no session, so it works here too. */}
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* The common case: registration signs the owner in immediately, and
          they click the verification link minutes later, still signed in. */}
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      {/* A broadcast email's unsubscribe link needs no session either, and
          the person clicking it may well be signed into their own account
          in another tab. */}
      <Route path="/unsubscribe" element={<UnsubscribePage />} />
      {/* Print-only pages render outside `AppShell` — no sidebar or nav to
          hide, since `print.css` never shows them on the printed page
          anyway, but the on-screen preview should still be the printable
          content alone, not the app chrome around it. */}
      <Route path="/people/print/directory" element={<PrintDirectoryPage />} />
      <Route path="/people/print/labels" element={<PrintLabelsPage />} />
      <Route path="/people/:id/print/credential" element={<PrintCredentialPage />} />
      <Route path="/calendar/print" element={<PrintCalendarPage />} />
      {/* Same reasoning as `/unsubscribe` above: a giving link carries no
          session requirement of its own, and the person opening it may
          well be signed into their own, unrelated church already. */}
      <Route path="/give/:tenantSlug" element={<GivePage />} />
      <Route path="/giving/success" element={<GiveSuccessPage />} />
      <Route path="/giving/cancelled" element={<GiveCancelledPage />} />
      <Route path="/give/manage/:token" element={<ManageRecurringPage />} />
      <Route path="/s/:token" element={<PublicSharePage />} />
    </Routes>
  );
}
